/*
 * ez-video-instant: the product video card and its popup (sections/ez-video-instant.liquid).
 *
 * Reads everything from the section's markup, so it can run on any markup of that shape:
 *   [data-ez-vi]                 root; data-ez-vi-kind (stream | mp4 | placeholder), data-ez-vi-delay
 *                                (seconds), data-ez-vi-product-id, data-ez-vi-product-name,
 *                                data-ez-vi-handle, data-ez-vi-has-sound (the video was kept with sound),
 *                                data-ez-vi-editor (in the theme editor). Since 2026-10-28, for the player:
 *                                data-ez-vi-delivery (adaptive | mp4, the section's Video delivery),
 *                                data-ez-vi-hls (the HLS manifest, a Stream video only), data-ez-vi-lib
 *                                (the hls.js asset), data-ez-vi-still (the full video's frame 0 at 720p or
 *                                more, a Stream video only) and data-ez-vi-own (our own MP4 on the media
 *                                host, the last source). Markup without them plays the single MP4.
 *   [data-ez-vi-card]            the fixed card, off the screen, transparent and out of the Tab order until
 *                                it slides in (no hidden attribute in the markup)
 *   [data-ez-vi-card-video]      the card's muted loop; carries its src and poster in the markup with
 *                                preload none, and starts loading only when the card shows. Without a
 *                                popup video it is the one video: it moves into the popup's frame while
 *                                the popup is open and back after
 *   [data-ez-vi-pop-video]       only when the card loops a light loop file: the popup's own video, the
 *                                full MP4 in data-ez-vi-src. Its poster attribute is dropped: the popup
 *                                never shows a picture as low as the card's
 *   [data-ez-vi-open] / [data-ez-vi-dismiss]
 *   [data-ez-vi-dialog]          the popup (a dialog element), with [data-ez-vi-backdrop],
 *                                [data-ez-vi-frame], [data-ez-vi-media] (the video's box),
 *                                [data-ez-vi-loading], [data-ez-vi-toggle], [data-ez-vi-sound] (only for a
 *                                video with sound), [data-ez-vi-close], the caption's
 *                                [data-ez-vi-len-wrap] / [data-ez-vi-len] (the length, filled from the
 *                                video when the details gave none) and [data-ez-vi-share], the sharing
 *                                code's empty mount in the caption strip (assets/ez-share.js fills it)
 *
 * The popup's frame grows out of the card when it opens and shrinks back into it when it closes
 * (300 ms in, 220 ms out; a plain fade under reduced motion). The card always plays muted. The popup plays
 * muted with no sound control, or, for a video with sound, with sound from the tap and a sound button.
 *
 * How it draws (2026-10-28, catalog/video/research/2026-10-playback/notes/motion.md): every motion is
 * transform or opacity only, so the compositor runs it whatever the main thread is doing; the frame keeps
 * its 16px corner and cross-fades with the card. On the live product page any class change costs a
 * whole-page style recalc, so the popup's states are data- attributes and Web Animations, never classes.
 * A tap reads first, calls play() inside the gesture, does its DOM writes in one batch, reads once, starts
 * the motion and leaves the rest (the event, the scroll lock, the still) until after the first frame.
 * A close pauses first; the card fades back as the frame lands, and the dialog shuts after. The blur sits
 * on a layer of its own that comes in once the frame has landed, and not at all on a phone with 2 GB or
 * less or under reduced transparency (a 72% dim instead). The loading ring waits 300 ms and the stall
 * ring 500 ms (in the CSS), so a fast start or a short stall never flashes one. A double tap, Back during
 * the grow, a turn of the phone mid-motion and a hidden page are each handled. Since 2026-11-04
 * (catalog/video/research/2026-11-stalls/notes/motion.md) the dialog opens as a popover where the browser
 * has one and the markup carries popover="manual": showModal() re-styled the whole page in the tap. Esc,
 * the Tab trap and Back are the JS's own on that path.
 *
 * The player (Player below, since 2026-10-28). Sources, each tried when the one before it fails:
 *   hls   Stream's HLS ladder through hls.js (the light build, a theme asset loaded only when needed),
 *         where MediaSource or ManagedMediaSource exists and Video delivery is adaptive (or ?ez-vi=adaptive);
 *         never the browser's own HLS, which can't hold the floor. A video with sound skips it (the light
 *         build plays no separate audio)
 *   file  the single MP4 (Stream's downloads/default.mp4, or our own file for the mp4 link form)
 *   own   our own MP4 on the media host, when it is another file
 * then the error state with "Try again". The floor: hls plays only the rungs whose short side is 720 px
 * or more (720p and 1080p), climbs to 1080p only with 8 s or more ahead and on hls.js's own measured
 * estimate, and is capped only where a rung would be more than the screen's own pixels. Since 2026-11-04
 * (catalog/video/research/2026-11-stalls/): the start plays at once when what is loaded reaches the end at
 * the line's measured speed, else it waits behind the loading state until it does, 6 s after the tap at
 * most; a stall that ran dry waits the same way, 6 s at most, with 3 s ahead. On hls the speed is hls.js's
 * estimate x 0.8 with its first-byte estimate, on an MP4 the rate its buffer has filled at.
 * The spinner shows 500 ms into a stall (the waiting event or the time not moving) and goes the moment the
 * time moves. While it loads, the popup shows the full video's own frame 0 at 720p or more, dimmed under
 * the spinner (the Stream still, or the video's own first frame), else the dark skeleton. Offline, it says
 * so and carries on at online. Nothing is ever a spinner that doesn't end.
 *
 * Before the tap: once the page has loaded and gone idle with the card on or near the screen, or at the
 * first touch of the card, the popup's source starts: the manifest and its first two segments at 720p
 * (hls, 8 s), then it stops, or the MP4 with preload auto, plus the still. Never under Save-Data or 2g,
 * where it loads at the tap.
 *
 * History: opening adds one entry. The browser's Back (popstate) closes the popup and leaves the visitor
 * on the page; every other close goes back through that entry, so none is left behind. #up-close in the
 * address opens the popup at start, muted (also when the card was dismissed), and Back closes it.
 * The root sends bubbling ez-vi:open and ez-vi:close (detail {productId}) and opens on ez-vi:open-request.
 *
 * window.EzVideoInstant.init(root) starts one root; initAll(scope) starts every root not yet started.
 * Both run by themselves on load and on the theme editor's section load.
 *
 * GA4, only when gtag is a function and never in the editor: video_instant_shown (once per page view,
 * when the card has slid in and the page is visible), video_instant_open, video_instant_close
 * (seconds_watched, percent_watched, close_method: button, backdrop, esc, back, other or leave) and
 * video_instant_dismiss, each with product_id, product_name and video_type. Each open gets exactly one
 * close: when the page is hidden or left with the popup open, the close goes then, as close_method leave
 * with transport_type beacon, and a later close of that same open sends nothing. Since 2026-10-28 the close
 * also carries the playback's numbers (first_frame_ms, stall_count, stall_ms, open_lag_ms, open_max_gap_ms,
 * video_h, and since 2026-11-04 first_stall_s, where in the video the first stall of 0.5 s or more began)
 * and end_state; shown, open and close carry net_type; and every event carries
 * traffic_type internal on a device flagged with ?ez_internal=1 (?ez_internal=0 clears it). Each event is
 * mirrored to Clarity (clarity('event'), with clarity('set') tags at the close) when Clarity is on the page.
 * ?es_debug=1 logs the player to the console.
 *
 * The label test (data-ez-vi-ab on the root, initiatives/experiments/video-card-label/): each device is put
 * once in up_close or in_hand (localStorage ez_ab:<test>); in_hand swaps the card's label and the caption's
 * words before the card slides in, and every event adds ab_test and ab_arm. A device whose storage fails sees
 * the label and is in no arm. ?ez_ab=up_close or ?ez_ab=in_hand shows that arm on this page only, for
 * checking, and its events carry no arm.
 */
(function () {
  'use strict';

  if (window.EzVideoInstant) {
    window.EzVideoInstant.initAll(document);
    return;
  }

  var DISMISS_KEY = 'ez-vi-dismissed';
  var DELIVERY_KEY = 'ez-vi-delivery';
  var INTERNAL_KEY = 'ez_internal';
  var HASH = '#up-close';

  var FLOOR = 720;            // short side, px: the popup plays the 720p and 1080p rungs, never a lower one
  var PRIME_SEGS = 2;         // before the tap: two segments at 720p (8 s of Stream's 4 s ones), then it stops
  var PRIME_S = 6;            // the prime's buffer goal, between one segment and two, so hls.js asks for no third
  var CLIMB_S = 8;            // 1080p only with this much ahead; below it, the 720p rung
  var PLAY_S = 30;            // the buffer goal once it plays
  var STALL_SHOW_MS = 500;    // a stall shows the spinner after this
  var DRY_S = 0.5;            // a stall with less than this ahead has run out of data: it waits to fill again
  var RESUME_MIN_S = 3;       // a stall plays on only with this much ahead, or the rest of the video
  var WAIT_MAX_MS = 6000;     // a start, or a stall that ran dry, waits at most this long to reach the end
  var LINE_SHARE = 0.8;       // the share of hls.js's estimate a wait counts on
  var HOLD_IDLE_MS = 4000;    // a hold with nothing arriving for this long lets the browser play what it has
  var STUCK_MS = 12000;       // nothing arriving for this long, online: reload in place, then the next source
  var STUCK_SLOW_MS = 45000;  // the same where the bytes of a segment can't be seen arriving

  var WORDS = {
    error: 'The video didn’t load.',
    retry: 'Try again',
    offline: 'You’re offline. The video will carry on when you’re back online.'
  };

  var motionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function reducedMotion() {
    return !!(motionQuery && motionQuery.matches);
  }

  function now() {
    return window.performance && typeof performance.now === 'function' ? performance.now() : Date.now();
  }

  // Save-Data on, or a 2g or slow-2g connection
  function lowData() {
    var c = navigator.connection;
    return !!(c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || '')));
  }

  // the connection as the browser reads it: slow-2g, 2g, 3g, 4g, save-data, or unknown (no API: Safari, Firefox)
  function netType() {
    var c = navigator.connection;
    if (!c) return 'unknown';
    if (c.saveData) return 'save-data';
    var t = c.effectiveType || '';
    return t === 'slow-2g' || t === '2g' || t === '3g' || t === '4g' ? t : 'unknown';
  }

  function offline() {
    return navigator.onLine === false;
  }

  var query = '';
  try {
    query = window.location.search || '';
  } catch (e) {}

  function param(name) {
    var m = new RegExp('[?&]' + name + '=([^&#]*)').exec(query);
    return m ? m[1] : null;
  }

  // a flag kept in storage, set and cleared from the address: ?name=on sets it, ?name=0 clears it
  function flag(store, name, key, on) {
    var v = param(name);
    try {
      var s = window[store];
      if (v === on) s.setItem(key, on);
      else if (v === '0' || v === 'default') s.removeItem(key);
      return s.getItem(key);
    } catch (e) {
      return v === on ? on : null;
    }
  }

  // ?ez_internal=1 marks this device as the owner's: the card's events carry traffic_type internal
  var internal = flag('localStorage', 'ez_internal', INTERNAL_KEY, '1') === '1';
  // ?ez-vi=adaptive or ?ez-vi=mp4 overrides the section's Video delivery for the browsing session
  var deliveryOverride = (function () {
    var v = param('ez-vi');
    try {
      if (v === 'adaptive' || v === 'mp4') window.sessionStorage.setItem(DELIVERY_KEY, v);
      else if (v === 'default') window.sessionStorage.removeItem(DELIVERY_KEY);
      return window.sessionStorage.getItem(DELIVERY_KEY);
    } catch (e) {
      return v === 'adaptive' || v === 'mp4' ? v : null;
    }
  })();
  var debug = flag('sessionStorage', 'es_debug', 'es_debug', '1') === '1';

  // this device's arm of a test, flipped once and kept; null when it can't be kept, so the device is in no arm
  function abArm(test) {
    var key = 'ez_ab:' + test;
    try {
      var s = window.localStorage;
      var arm = s.getItem(key);
      if (arm === 'up_close' || arm === 'in_hand') return arm;
      arm = Math.random() < 0.5 ? 'up_close' : 'in_hand';
      s.setItem(key, arm);
      return s.getItem(key) === arm ? arm : null;
    } catch (e) {
      return null;
    }
  }

  function log() {
    if (!debug) return;
    try {
      console.info.apply(console, ['[ez-vi]', Math.round(now())].concat(Array.prototype.slice.call(arguments)));
    } catch (e) {}
  }

  function wasDismissed() {
    try {
      return window.sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function rememberDismissed() {
    try {
      window.sessionStorage.setItem(DISMISS_KEY, '1');
    } catch (e) {}
  }

  // m:ss, as the caption and the note under the description write a length
  function clock(seconds) {
    var s = Math.round(seconds);
    var m = Math.floor(s / 60);
    var r = s % 60;
    return m + ':' + (r < 10 ? '0' : '') + r;
  }

  // when the page has finished loading and the browser is idle
  function afterLoadIdle(fn) {
    function idle() {
      if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(fn, { timeout: 3000 });
      else setTimeout(fn, 300);
    }
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  }

  // after the next frame is drawn, off the tap's own task; a hidden page draws no frames, so a timer too
  function afterFrame(fn) {
    var done = false;
    function run() {
      if (done) return;
      done = true;
      fn();
    }
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(function () {
        setTimeout(run, 0);
      });
    }
    setTimeout(run, 120);
  }

  // seconds loaded ahead of the playhead
  function aheadOf(v) {
    try {
      var b = v.buffered;
      var t = v.currentTime || 0;
      for (var i = 0; i < b.length; i++) {
        if (b.start(i) <= t + 0.25 && b.end(i) > t) return b.end(i) - t;
      }
    } catch (e) {}
    return 0;
  }

  function seekWhenReady(v, t) {
    if (!(t > 0)) return;
    function go() {
      v.removeEventListener('loadedmetadata', go);
      try {
        v.currentTime = t;
      } catch (e) {}
    }
    if (v.readyState >= 1) go();
    else v.addEventListener('loadedmetadata', go);
  }

  function canMse() {
    var MS = window.ManagedMediaSource || window.MediaSource;
    try {
      return !!(MS && typeof MS.isTypeSupported === 'function' && MS.isTypeSupported('video/mp4; codecs="avc1.42E01E"'));
    } catch (e) {
      return false;
    }
  }

  var libLoads = {};

  function loadLib(url) {
    if (window.Hls) return Promise.resolve(window.Hls);
    if (libLoads[url]) return libLoads[url];
    libLoads[url] = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = url;
      s.async = true;
      s.onload = function () {
        if (window.Hls) resolve(window.Hls);
        else {
          libLoads[url] = null;
          reject(new Error('no Hls'));
        }
      };
      s.onerror = function () {
        libLoads[url] = null;
        reject(new Error('hls.js did not load'));
      };
      document.head.appendChild(s);
    });
    return libLoads[url];
  }

  // hls.js's own loader, told to say each time bytes of a playlist or segment arrive (and which loader)
  function watchedLoader(Base, onBytes) {
    if (typeof Base !== 'function') return null;
    return function (config) {
      var l = new Base(config);
      var lp = l.loadprogress;
      if (typeof lp === 'function') {
        l.loadprogress = function () {
          try {
            onBytes(l);
          } catch (e) {}
          return lp.apply(l, arguments);
        };
      }
      return l;
    };
  }

  // the popup video's height on screen in device pixels, worked out the way the CSS sizes the frame
  function screenPx(ar, capPx) {
    var vw = window.innerWidth || 0;
    var vh = window.innerHeight || 0;
    var w = Math.min(vw - 32, (Math.min(0.86 * vh, 860) - capPx) * ar);
    var dpr = Math.min(window.devicePixelRatio || 1, 3);
    return Math.round((w / ar) * dpr);
  }

  function shortSide(w, h) {
    return Math.min(w || h || 0, h || w || 0);
  }

  // ---------- the player: one video element, one chain of sources ----------

  function Player(v, o) {
    this.v = v;
    this.o = o; // chain, hls, file, own, lib, ar, capPx, attached, on(event, data)
    this.chain = o.chain;
    this.i = -1;
    this.path = null;
    this.hls = null;
    this.Hls = null;
    this.priming = false;
    this.primed = false;
    this.primeFrags = 0;
    this.wantPlay = false;
    this.gate = false;      // the start waits for its go, decided after the tap's first frame
    this.startHold = false; // the start is held until what is loaded reaches the end
    this.playAt = 0;
    this.holding = false;
    this.holdAt = 0;
    this.noHold = false;
    this.fragsIn = 0;       // segments buffered on this hls.js, so its estimate is a measured one
    this.flight = null;     // the hls.js loader of the segment arriving now
    this.screenCap = -1;
    this.meter = null;      // the single file: how fast its buffer has filled, in seconds of video a second
    this.failed = false;
    this.restart = false;
    this.nudged = false;
    this.recovered = false;
    this.bytesSeen = false;
    this.wasOffline = false;
    this.lastT = 0;
    this.movedAt = 0;
    this.progressAt = 0;
    this.lastAhead = 0;
    this.pathFrame = false;
    this.tick = null;
    this.vfc = 0;
    this.levels = [];
    this.heights = [];
    this.reset();
    this.bind();
  }

  // the measures of one open
  Player.prototype.reset = function () {
    this.first = false;
    this.firstAt = 0;
    this.videoH = 0;
    this.stalled = false;
    this.stallAt = 0;
    this.stallPos = 0;
    this.stallCount = 0;
    this.stallMs = 0;
    this.firstStallS = null; // where in the video the first stall of STALL_SHOW_MS or more began
    this.errorCode = 0;
  };

  Player.prototype.emit = function (name, data) {
    log(name, data === undefined ? '' : data);
    try {
      if (this.o.on) this.o.on(name, data);
    } catch (e) {}
  };

  Player.prototype.bind = function () {
    var self = this;
    var v = this.v;
    v.addEventListener('timeupdate', function () { self.moved(); });
    v.addEventListener('playing', function () { self.moved(); });
    v.addEventListener('waiting', function () {
      if (self.wantPlay && !v.seeking) self.stall('waiting');
    });
    v.addEventListener('seeking', function () { self.movedAt = now(); });
    v.addEventListener('progress', function () {
      self.progressAt = now();
      self.metre();
    });
    // the browser stopped loading by itself: the time until it loads again isn't the line's
    v.addEventListener('suspend', function () {
      if (self.meter) self.meter.at = 0;
    });
    v.addEventListener('loadeddata', function () {
      self.progressAt = now();
      self.emit('loaded');
    });
    v.addEventListener('resize', function () {
      if (!v.paused) self.played(v.videoHeight);
    });
    v.addEventListener('error', function () { self.onError(); });
    window.addEventListener('online', function () { self.onOnline(); });
  };

  Player.prototype.played = function (h) {
    if (h > 0 && this.heights[this.heights.length - 1] !== h) {
      this.heights.push(h);
      this.emit('height', h);
    }
  };

  // ----- starting a source -----

  Player.prototype.prime = function () {
    if (this.path || this.wantPlay || this.failed) return;
    this.priming = true;
    this.start(0, 0);
  };

  Player.prototype.start = function (i, at) {
    var self = this;
    var v = this.v;
    var o = this.o;
    this.dropHls();
    this.i = i;
    this.path = this.chain[i] || null;
    this.nudged = false;
    this.noHold = false;
    this.restart = false;
    this.recovered = false;
    this.bytesSeen = false;
    this.pathFrame = false;
    this.progressAt = now();
    this.lastAhead = 0;
    this.primeFrags = 0;
    this.flight = null;
    this.meter = { at: 0, end: 0, ms: 0, s: 0 };
    if (!this.path) return this.fail();
    this.emit('path', this.path);
    this.holding = false;
    if (this.path === 'hls') {
      loadLib(o.lib).then(function (Hls) {
        if (self.path !== 'hls' || self.i !== i) return;
        if (!Hls.isSupported()) return self.next(4, 'hls unsupported');
        self.attach(Hls, at);
      }, function () {
        if (self.path === 'hls' && self.i === i) self.next(4, 'hls.js did not load');
      });
      return;
    }
    var url = this.path === 'own' ? o.own : o.file;
    v.preload = 'auto';
    if (!(o.attached && this.path === 'file') && v.getAttribute('src') !== url) v.setAttribute('src', url);
    seekWhenReady(v, at);
    if (this.priming && !this.wantPlay) {
      this.priming = false;
      this.primed = true;
    }
  };

  Player.prototype.attach = function (Hls, at) {
    var self = this;
    var v = this.v;
    var o = this.o;
    // the line's estimate is hls.js's own, from the segments it measures (never the browser's downlink)
    var cfg = {
      autoStartLoad: false,
      enableWorker: false,
      startFragPrefetch: false,
      capLevelToPlayerSize: false,
      maxBufferLength: this.wantPlay ? PLAY_S : PRIME_S,
      maxMaxBufferLength: this.wantPlay ? 60 : PRIME_S,
      backBufferLength: 90,
      // the loop replays the start it holds: hls.js would drop it after a climb and load it again at 1080p,
      // a freeze at the loop
      loopBackBufferFlush: false,
      abrBandWidthUpFactor: 0.8
    };
    var L = watchedLoader(Hls.DefaultConfig && Hls.DefaultConfig.loader, function (l) {
      self.bytesSeen = true;
      self.progressAt = now();
      if (l && l.context && l.context.frag) self.flight = l;
    });
    if (L) cfg.loader = L;
    var hls;
    try {
      hls = new Hls(cfg);
    } catch (e) {
      return this.next(4, 'hls init');
    }
    this.hls = hls;
    this.Hls = Hls;
    this.fragsIn = 0;
    var E = Hls.Events;
    try {
      v.disableRemotePlayback = true; // ManagedMediaSource on iPhone needs it
    } catch (e) {}
    hls.on(E.MANIFEST_PARSED, function () {
      if (self.hls !== hls) return;
      self.progressAt = now();
      self.floor();
      // the first segment is always the 720p rung, the lowest kept (hls.js's own first level still counts
      // the rungs the floor removed); the line's estimate climbs from the second
      hls.startLevel = 0;
      hls.startLoad(at || 0);
    });
    hls.on(E.FRAG_LOADED, function () { self.progressAt = now(); });
    // the next segment's rung is picked as soon as one is buffered: the cap is set from the buffer by then
    hls.on(E.BUFFER_APPENDED, function () {
      if (self.hls === hls) self.climb();
    });
    hls.on(E.FRAG_BUFFERED, function (e, d) {
      if (self.hls !== hls || (d && d.frag && d.frag.sn === 'initSegment')) return;
      self.progressAt = now();
      self.fragsIn++;
      if (!self.wantPlay && self.priming && ++self.primeFrags >= PRIME_SEGS) {
        hls.stopLoad();
        self.priming = false;
        self.primed = true;
        self.emit('primed', self.levels);
      }
    });
    hls.on(E.FRAG_CHANGED, function (e, d) {
      var l = d && d.frag && hls.levels[d.frag.level];
      if (l && self.wantPlay) self.played(l.height);
    });
    hls.on(E.LEVEL_SWITCHED, function (e, d) {
      var l = hls.levels[d.level];
      log('level', l ? l.width + 'x' + l.height : d.level);
    });
    hls.on(E.ERROR, function (e, d) {
      if (self.hls !== hls || !d || !d.fatal) return;
      var T = Hls.ErrorTypes;
      if (d.type === T.MEDIA_ERROR && !self.recovered) {
        self.recovered = true;
        self.errorCode = 3;
        try {
          hls.recoverMediaError();
          return;
        } catch (x) {}
      }
      var code = d.type === T.NETWORK_ERROR ? 2 : d.type === T.MEDIA_ERROR ? 3 : 4;
      if (code === 2 && offline()) {
        // offline: start this source again at online, never use up the chain on it
        self.errorCode = 2;
        self.restart = true;
        return;
      }
      self.next(code, d.details);
    });
    hls.attachMedia(v);
    hls.loadSource(o.hls);
    if (this.wantPlay) this.kick();
  };

  // only the rungs at 720p or more; the cap at the screen's own pixels, never below them
  Player.prototype.floor = function () {
    var hls = this.hls;
    var lv = (hls && hls.levels) || [];
    var keep = 0;
    var top = -1;
    var i;
    for (i = 0; i < lv.length; i++) {
      if (shortSide(lv[i].width, lv[i].height) >= FLOOR) keep++;
      if (top < 0 || lv[i].height > lv[top].height) top = i;
    }
    for (i = lv.length - 1; i >= 0; i--) {
      var ok = keep ? shortSide(lv[i].width, lv[i].height) >= FLOOR : i === top;
      if (!ok) {
        try {
          hls.removeLevel(i);
        } catch (e) {}
      }
    }
    this.cap();
  };

  Player.prototype.cap = function () {
    var hls = this.hls;
    var lv = (hls && hls.levels) || [];
    if (!lv.length) return;
    var need = screenPx(this.o.ar || 0.5625, this.o.capPx || 0);
    var cap = -1;
    var top = 0;
    for (var i = 0; i < lv.length; i++) {
      if (lv[i].height > lv[top].height) top = i;
      if (lv[i].height >= need && (cap < 0 || lv[i].height < lv[cap].height)) cap = i;
    }
    this.screenCap = cap < 0 || cap === top ? -1 : cap;
    this.climb();
    this.levels = lv.map(function (l) { return l.width + 'x' + l.height; });
    log('rungs', this.levels.join(' '), 'screen', need + 'px', 'cap', this.screenCap);
  };

  // 1080p only once it plays and with CLIMB_S or more ahead, where hls.js's own rule decides; below it, and
  // while it primes (so not even a 1080p playlist loads before the tap), the 720p rung
  Player.prototype.climb = function () {
    var hls = this.hls;
    if (!hls || !hls.levels || !hls.levels.length) return;
    var c = this.wantPlay && aheadOf(this.v) + 0.1 >= CLIMB_S ? this.screenCap : 0;
    if (hls.autoLevelCapping !== c) hls.autoLevelCapping = c;
  };

  // ----- the tap -----

  // Called inside the tap, synchronously: play() there is what lets a video with sound speak. Loading
  // a source and attaching hls.js come after the tap's first frame. at: the tap's own time, which a held
  // start's WAIT_MAX_MS counts from.
  Player.prototype.play = function (at) {
    var self = this;
    var v = this.v;
    // a prime still loading goes on loading for the play; starting it again would throw its segment away
    var wasPriming = this.priming;
    this.priming = false;
    this.wantPlay = true;
    this.reset();
    if (this.failed) {
      this.failed = false;
      this.dropHls();
      this.path = null;
      this.i = -1;
    }
    if ((v.currentTime || 0) > 0.05) {
      try {
        v.currentTime = 0;
      } catch (e) {}
    }
    this.lastT = 0;
    this.pathFrame = false; // each open shows its own first frame again
    this.movedAt = now();
    this.progressAt = now();
    this.playAt = at > 0 ? at : now();
    if (!this.path && this.chain[0] !== 'hls') this.start(0, 0); // an MP4's src is cheap to set in the tap
    // whether it starts at once or waits is worked out after the tap's first frame, never in the tap
    this.gate = true;
    this.startHold = false;
    this.doPlay();
    afterFrame(function () {
      if (!self.wantPlay) return;
      if (!self.path) self.start(0, 0);
      else if (self.hls) {
        var c = self.hls.config;
        c.maxBufferLength = PLAY_S;
        c.maxMaxBufferLength = 60;
        self.cap();
        if (!wasPriming && self.hls.levels && self.hls.levels.length) self.hls.startLoad(v.currentTime || 0);
      } else if (self.restart) self.start(self.i, 0);
      self.decideStart();
    });
    this.watch();
  };

  // The start: at once when what is loaded reaches the end at the line's measured speed; else held behind
  // the loading state until it does, WAIT_MAX_MS after the tap at most. play() already ran in the tap, so
  // a held start is unlocked for its later play on a phone that wants a gesture.
  Player.prototype.decideStart = function () {
    if (!this.gate || !this.wantPlay || this.holding) return;
    var v = this.v;
    if (this.noHold || now() - this.playAt >= WAIT_MAX_MS || this.wait(aheadOf(v)) <= 0) return this.go();
    this.startHold = true;
    this.hold();
    if ((v.currentTime || 0) > 0.05) {
      try {
        v.currentTime = 0;
      } catch (e) {}
    }
  };

  // the start's go: a held start plays; one already playing shows its frame now
  Player.prototype.go = function () {
    this.gate = false;
    this.startHold = false;
    if (this.holding) return this.release();
    this.lastT = -1;
    this.moved();
  };

  Player.prototype.doPlay = function () {
    var self = this;
    var v = this.v;
    var p;
    try {
      p = v.play();
    } catch (e) {
      p = null;
    }
    if (p && typeof p.then === 'function') {
      p.then(null, function (err) {
        // only NotAllowedError is the browser refusing; a source that can't play is the chain's to handle
        if (!self.wantPlay || self.holding || !err || err.name !== 'NotAllowedError') return;
        if (!v.muted) {
          // sound refused: play it muted, and the sound button turns it on
          v.muted = true;
          self.emit('muted');
          self.doPlay();
          return;
        }
        self.errorCode = 9;
        self.wantPlay = false;
        self.emit('refused');
      });
    }
  };

  Player.prototype.kick = function () {
    if (this.wantPlay && !this.holding) this.doPlay();
  };

  // ----- holding, stalls and the spinner -----

  // Seconds still to wait before playing on reaches the end of the video with no stall, at the line's
  // measured speed: 0 or less to go now, Infinity while the speed or the length isn't known yet.
  Player.prototype.wait = function (ahead) {
    var v = this.v;
    var d = v.duration;
    var t = v.currentTime || 0;
    if (!(isFinite(d) && d > 0)) return Infinity;
    var end = t + ahead;
    if (end >= d - 0.1) return 0;
    if (this.path === 'hls') return this.hls ? this.waitHls(t, end) : Infinity;
    return this.waitFile(ahead, d - end);
  };

  // hls: segments arrive whole, so the wait is the largest, over the segments still to load, of the time to
  // load through that segment less the time until playback reaches it. The speed is hls.js's estimate x
  // LINE_SHARE with its first-byte estimate per segment; a segment's bytes are its rung's measured rate,
  // and the one arriving now counts only what is still to come.
  Player.prototype.waitHls = function (t, end) {
    var hls = this.hls;
    var bw = (hls.bandwidthEstimate * LINE_SHARE) / 8;
    if (!this.fragsIn || !(bw > 0)) return Infinity;
    var ttfb = (hls.ttfbEstimate || 0) / 1000;
    var li = hls.loadLevel;
    var lv = hls.levels && hls.levels[li >= 0 ? li : 0];
    var det = lv && lv.details;
    var Bps = lv ? (lv.realBitrate || lv.bitrate || 0) / 8 : 0;
    if (!det || !det.fragments || !(Bps > 0)) return Infinity;
    var f = this.flight;
    var fs = f && f.stats;
    var inFrag = f && f.context && fs && !fs.aborted && !(fs.loading && fs.loading.end > 0) ? f.context.frag : null;
    var load = 0;
    var worst = -Infinity;
    for (var i = 0; i < det.fragments.length; i++) {
      var fr = det.fragments[i];
      if (fr.start + fr.duration <= end + 0.1) continue; // here already
      var bytes = Bps * fr.duration;
      if (inFrag && inFrag.sn === fr.sn && inFrag.level === li) {
        if (!(fs.loaded > 0)) load += ttfb;
        bytes = Math.max(0, (fs.total > 0 ? fs.total : bytes) - (fs.loaded || 0));
      } else {
        load += ttfb;
      }
      load += bytes / bw;
      worst = Math.max(worst, load - Math.max(0, fr.start - t));
    }
    return worst;
  };

  // the single file: the rate its buffer has filled at, in seconds of video a second, while the browser loaded
  Player.prototype.metre = function () {
    var m = this.meter;
    if (!m || this.path === 'hls') return;
    var t = now();
    var end = (this.v.currentTime || 0) + aheadOf(this.v);
    if (m.at && end >= m.end) {
      m.ms += t - m.at;
      m.s += end - m.end;
    }
    m.at = t;
    m.end = end;
  };

  // the single file arrives as it plays, so the worst point is the end: left x (1/rate - 1) - ahead
  Player.prototype.waitFile = function (ahead, left) {
    var m = this.meter;
    if (!m || m.ms < 300) return Infinity;
    var r = m.s / (m.ms / 1000);
    if (!(r > 0)) return Infinity;
    return r >= 1 ? -ahead : left * (1 / r - 1) - ahead;
  };

  // whether a hold plays on: a held start once what is loaded reaches the end, or WAIT_MAX_MS after the
  // tap; a stall that ran dry the same from when it began, and only with RESUME_MIN_S ahead (or the rest)
  Player.prototype.canGo = function (ahead) {
    if (this.startHold) return now() - this.playAt >= WAIT_MAX_MS || this.wait(ahead) <= 0;
    var v = this.v;
    var d = v.duration;
    var rest = isFinite(d) && d > 0 ? Math.max(0, d - (v.currentTime || 0)) : Infinity;
    if (ahead < Math.min(RESUME_MIN_S, rest) - 0.05) return false;
    return now() - this.holdAt >= WAIT_MAX_MS || this.wait(ahead) <= 0;
  };

  Player.prototype.hold = function () {
    if (this.holding || this.noHold) return;
    var v = this.v;
    this.holding = true;
    this.holdAt = now();
    if (!v.paused) {
      try {
        v.pause();
      } catch (e) {}
    }
  };

  Player.prototype.release = function () {
    if (!this.holding) return;
    this.holding = false;
    this.gate = false;
    this.startHold = false;
    this.movedAt = now();
    this.doPlay();
  };

  // A stall: the picture stopped. Only one that has run out of data mid-play waits behind the spinner,
  // until enough is ahead to reach the end (canGo); one with data still there (a decoder or a busy page
  // catching up) plays on by itself.
  Player.prototype.stall = function (why) {
    if (!this.wantPlay) return;
    if (this.first && !this.stalled) {
      this.stalled = true;
      this.stallAt = now();
      this.stallPos = this.v.currentTime || 0;
      this.stallCount++;
      this.emit('stall', why + ' ' + aheadOf(this.v).toFixed(2) + 's ahead');
    }
    if (this.pathFrame && aheadOf(this.v) < DRY_S) this.hold();
  };

  Player.prototype.endStall = function () {
    if (!this.stalled) return;
    this.stalled = false;
    var ms = now() - this.stallAt;
    if (ms >= STALL_SHOW_MS && this.firstStallS === null) this.firstStallS = this.stallPos;
    this.stallMs += ms;
    this.emit('stallend', Math.round(this.stallMs));
  };

  Player.prototype.moved = function () {
    var v = this.v;
    var t = v.currentTime || 0;
    if (t === this.lastT) return;
    this.lastT = t;
    this.movedAt = now();
    this.progressAt = this.movedAt;
    if (!this.wantPlay || v.paused || this.holding || this.gate) return;
    if (!this.pathFrame && t > 0) {
      this.pathFrame = true;
      if (!this.first) this.firstFrame(now());
      this.emit('frame');
    }
    this.endStall();
  };

  Player.prototype.firstFrame = function (at) {
    if (this.first) return;
    this.first = true;
    this.firstAt = at;
    this.videoH = this.v.videoHeight || 0;
    this.played(this.videoH);
  };

  // the first frame painted, where the browser says so
  Player.prototype.watchFrame = function () {
    var self = this;
    var v = this.v;
    if (typeof v.requestVideoFrameCallback !== 'function' || this.vfc) return;
    this.vfc = v.requestVideoFrameCallback(function (t) {
      self.vfc = 0;
      if (!self.wantPlay) return;
      if (v.paused || self.holding || self.gate) return self.watchFrame();
      self.firstFrame(t);
    });
  };

  Player.prototype.watch = function () {
    var self = this;
    clearInterval(this.tick);
    this.watchFrame();
    this.tick = setInterval(function () { self.step(); }, 250);
  };

  Player.prototype.unwatch = function () {
    clearInterval(this.tick);
    this.tick = null;
    if (this.vfc && typeof this.v.cancelVideoFrameCallback === 'function') {
      try {
        this.v.cancelVideoFrameCallback(this.vfc);
      } catch (e) {}
    }
    this.vfc = 0;
  };

  Player.prototype.step = function () {
    var v = this.v;
    if (!this.wantPlay || this.failed || document.hidden) return;
    this.moved();
    var t = now();
    var ahead = aheadOf(v);
    if (ahead > this.lastAhead + 0.01) this.progressAt = t;
    this.lastAhead = ahead;
    this.climb();
    if (this.gate && !this.holding) this.decideStart();
    if (this.holding) {
      if (this.canGo(ahead)) this.release();
      else if (t - this.progressAt >= HOLD_IDLE_MS && ahead >= 0.5 && !(this.hls && this.bytesSeen)) {
        // nothing arriving while it waits (a browser that won't load a paused video): let it play what it has
        this.noHold = true;
        this.release();
      }
    } else if (!v.paused && !v.seeking && !v.ended && t - this.movedAt >= STALL_SHOW_MS) {
      this.stall('watchdog'); // iOS doesn't always send waiting
    }
    var waiting = this.holding || this.stalled || !this.pathFrame;
    if (!waiting) {
      this.wasOffline = false;
      return;
    }
    if (offline()) {
      if (!this.wasOffline) {
        this.wasOffline = true;
        this.emit('offline');
      }
      this.progressAt = t;
      return;
    }
    if (this.wasOffline) this.onOnline();
    var limit = this.hls && !this.bytesSeen ? STUCK_SLOW_MS : STUCK_MS;
    if (t - this.progressAt >= limit) this.nudge();
  };

  // nothing arriving with the network up (often a dead connection after a Wi-Fi/4G switch): load it again
  // in place once, then the next source
  Player.prototype.nudge = function () {
    var v = this.v;
    if (this.nudged) return this.next(2, 'stuck');
    this.nudged = true;
    this.progressAt = now();
    this.reload();
  };

  Player.prototype.reload = function () {
    var v = this.v;
    var t = this.first ? v.currentTime || 0 : 0;
    if (this.hls) {
      this.hls.stopLoad();
      this.hls.startLoad(t);
    } else if (this.path) {
      try {
        v.load();
      } catch (e) {}
      seekWhenReady(v, t);
      this.holding = false;
      this.hold();
    }
  };

  Player.prototype.onOnline = function () {
    var wasOff = this.wasOffline;
    this.wasOffline = false;
    if (!this.wantPlay || this.failed) return;
    this.progressAt = now();
    if (wasOff) this.emit('online');
    if (this.restart) {
      this.start(this.i, this.first ? this.v.currentTime || 0 : 0);
      return;
    }
    if (this.holding || this.stalled || !this.pathFrame) this.reload();
  };

  Player.prototype.onError = function () {
    if (this.hls || !this.path) return; // hls.js reports through its own ERROR event
    var code = (this.v.error && this.v.error.code) || 4;
    if (code === 2 && offline()) {
      this.errorCode = 2;
      this.restart = true;
      return;
    }
    this.next(code, 'element error ' + code);
  };

  // the next source, from where the visitor was
  Player.prototype.next = function (code, why) {
    this.errorCode = code || this.errorCode;
    log('source failed', this.path, why);
    var at = this.first ? this.v.currentTime || 0 : 0;
    // the next source plays as soon as it can: a start's wait isn't begun again on it
    this.gate = false;
    this.startHold = false;
    if (this.i + 1 >= this.chain.length) return this.fail();
    this.start(this.i + 1, at);
    if (this.wantPlay) this.kick();
  };

  Player.prototype.fail = function () {
    this.failed = true;
    this.holding = false;
    this.gate = false;
    this.startHold = false;
    this.endStall();
    this.dropHls();
    this.path = null;
    this.emit('error', this.errorCode);
  };

  // "Try again": the chain again from the top, from where the visitor was
  Player.prototype.retry = function () {
    var at = this.first ? this.v.currentTime || 0 : 0;
    this.failed = false;
    this.wantPlay = true;
    this.holding = false;
    this.start(0, at);
    this.doPlay();
    this.watch();
  };

  // ----- the visitor's own pause and play, closing, the page hidden -----

  Player.prototype.paused = function () {
    if (this.holding || !this.wantPlay) return;
    this.wantPlay = false;
    this.endStall();
    this.unwatch();
  };

  Player.prototype.resume = function () {
    this.wantPlay = true;
    this.movedAt = now();
    this.progressAt = now();
    if (this.failed) return this.retry();
    this.doPlay();
    this.watch();
  };

  // the browser played it again by itself (after a background pause)
  Player.prototype.played_ = function () {
    if (this.wantPlay || this.failed) return;
    this.wantPlay = true;
    this.movedAt = now();
    this.watch();
  };

  // closing keeps what is buffered, so a reopen starts at once; it stops loading more
  Player.prototype.close = function () {
    this.wantPlay = false;
    this.holding = false;
    this.gate = false;
    this.startHold = false;
    this.endStall();
    this.unwatch();
    try {
      this.v.pause();
    } catch (e) {}
    if (this.hls) this.hls.stopLoad();
  };

  Player.prototype.sleep = function () {
    if (this.hls) this.hls.stopLoad();
  };

  Player.prototype.wake = function () {
    this.movedAt = now();
    this.progressAt = now();
    if (this.wantPlay && this.hls && this.hls.levels && this.hls.levels.length) this.hls.startLoad(this.v.currentTime || 0);
  };

  Player.prototype.dropHls = function () {
    if (!this.hls) return;
    try {
      this.hls.destroy();
    } catch (e) {}
    this.hls = null;
  };

  Player.prototype.destroy = function () {
    this.close();
    this.dropHls();
  };

  // what the close event carries, and what a test or ?es_debug=1 reads
  Player.prototype.report = function () {
    var open = this.stalled ? now() - this.stallAt : 0;
    var ms = this.stallMs + open;
    var at = this.firstStallS !== null ? this.firstStallS : open >= STALL_SHOW_MS ? this.stallPos : null;
    return {
      path: this.path,
      first: this.first,
      firstAt: this.firstAt,
      videoH: this.videoH,
      stallCount: this.stallCount,
      stallMs: ms,
      firstStallS: at,
      gate: this.gate,
      startHold: this.startHold,
      errorCode: this.errorCode,
      levels: this.levels.slice(),
      heights: this.heights.slice(),
      holding: this.holding,
      stalled: this.stalled,
      failed: this.failed,
      primed: this.primed
    };
  };

  var ENTER = 'cubic-bezier(0, 0, 0.2, 1)';
  var EXIT = 'cubic-bezier(0.4, 0, 1, 1)';

  // The blur costs the GPU a full-screen pass every frame: left out on a phone with 2 GB or less
  // (navigator.deviceMemory, Chrome and Android WebView only) and under reduced transparency.
  function skipBlur() {
    try {
      if (window.matchMedia && window.matchMedia('(prefers-reduced-transparency: reduce)').matches) return true;
    } catch (e) {}
    var mem = navigator.deviceMemory;
    return typeof mem === 'number' && mem <= 2;
  }

  // A state as a data- attribute (true for a bare one, or a value): on this page a class change re-styles
  // the whole document (custom-product-page.css's :has() rules over [class*=...]); an attribute only its
  // own rules use doesn't.
  function setFlag(el, name, value) {
    if (!el) return;
    if (value === false || value === null || value === undefined) {
      if (el.hasAttribute(name)) el.removeAttribute(name);
      return;
    }
    var v = value === true ? '' : String(value);
    if (el.getAttribute(name) !== v) el.setAttribute(name, v);
  }

  // Work that must not hold up the motion's first frame: runs once that frame is on the screen.
  // flushLater() runs it at once (the page is being hidden or left).
  var laterQueue = [];
  var laterArmed = false;

  function flushLater() {
    laterArmed = false;
    var q = laterQueue;
    laterQueue = [];
    for (var i = 0; i < q.length; i++) {
      try {
        q[i]();
      } catch (e) {}
    }
  }

  function afterPaint(fn) {
    laterQueue.push(fn);
    if (laterArmed) return;
    laterArmed = true;
    if (typeof window.requestAnimationFrame === 'function' && !document.hidden) {
      window.requestAnimationFrame(function () {
        setTimeout(flushLater, 0);
      });
    } else {
      setTimeout(flushLater, 0);
    }
  }

  var scrollLock = null;

  function lockScroll() {
    if (scrollLock) return;
    var html = document.documentElement;
    var body = document.body;
    scrollLock = { html: html.style.overflow, body: body.style.overflow, gutter: html.style.scrollbarGutter || '' };
    // a computer's scrollbar keeps its room, so the page under the scrim doesn't shift sideways
    html.style.scrollbarGutter = 'stable';
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
  }

  function unlockScroll() {
    if (!scrollLock) return;
    document.documentElement.style.overflow = scrollLock.html;
    document.body.style.overflow = scrollLock.body;
    document.documentElement.style.scrollbarGutter = scrollLock.gutter;
    scrollLock = null;
  }

  // the last Esc key, so a dialog cancel that no Esc came before is read as the phone's back
  var escAt = 0;
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' || e.key === 'Esc') escAt = Date.now();
  }, true);

  function wantsHash() {
    try {
      return (window.location.hash || '').toLowerCase() === HASH;
    } catch (e) {
      return false;
    }
  }

  function init(root) {
    if (!root || root.__ezVi) return root && root.__ezVi;

    var kind = root.getAttribute('data-ez-vi-kind') || '';
    var cardVideo = root.querySelector('[data-ez-vi-card-video]');
    var src = (cardVideo && cardVideo.getAttribute('src')) || root.getAttribute('data-ez-vi-src') || '';
    var poster = (cardVideo && cardVideo.getAttribute('poster')) || root.getAttribute('data-ez-vi-poster') || '';
    var editor = root.hasAttribute('data-ez-vi-editor');
    var hasSound = root.hasAttribute('data-ez-vi-has-sound');
    var delay = parseFloat(root.getAttribute('data-ez-vi-delay'));
    if (!(delay >= 0)) delay = 0;
    var productId = root.getAttribute('data-ez-vi-product-id') || '';
    var productName = root.getAttribute('data-ez-vi-product-name') || '';
    var handle = root.getAttribute('data-ez-vi-handle') || '';

    var abTest = editor ? null : root.getAttribute('data-ez-vi-ab');
    var abParams = null;
    if (abTest) {
      var abForced = param('ez_ab');
      if (abForced !== 'up_close' && abForced !== 'in_hand') abForced = null;
      var abShown = abForced || abArm(abTest);
      if (abShown && !abForced) abParams = { ab_test: abTest, ab_arm: abShown };
      if (abShown === 'in_hand') {
        var abLabel = root.querySelector('.ez-vi__label');
        var abWords = root.querySelector('[data-ez-vi-cap-words]');
        if (abLabel) abLabel.textContent = root.getAttribute('data-ez-vi-ab-label') || abLabel.textContent;
        if (abWords) abWords.textContent = root.getAttribute('data-ez-vi-ab-caption') || abWords.textContent;
      }
    }
    // the tilt is in the root's own inline style: read once, never through getComputedStyle at the tap
    var tilt = parseFloat(root.style.getPropertyValue('--ez-vi-tilt')) || 0;

    var card = root.querySelector('[data-ez-vi-card]');
    var openBtn = root.querySelector('[data-ez-vi-open]');
    var dismissBtn = root.querySelector('[data-ez-vi-dismiss]');
    var dialog = root.querySelector('[data-ez-vi-dialog]');
    var backdrop = root.querySelector('[data-ez-vi-backdrop]');
    var frame = root.querySelector('[data-ez-vi-frame]');
    var media = root.querySelector('[data-ez-vi-media]') || frame;
    var toggleBtn = root.querySelector('[data-ez-vi-toggle]');
    var soundBtn = root.querySelector('[data-ez-vi-sound]');
    var closeBtn = root.querySelector('[data-ez-vi-close]');
    var lenWrap = root.querySelector('[data-ez-vi-len-wrap]');
    var lenEl = root.querySelector('[data-ez-vi-len]');
    var ownPop = root.querySelector('[data-ez-vi-pop-video]');
    var fullSrc = ownPop ? ownPop.getAttribute('data-ez-vi-src') || '' : '';
    if (ownPop && !fullSrc) ownPop = null;

    if (!card || !openBtn || !dialog || !frame || !closeBtn) return null;
    if (kind !== 'placeholder' && (!src || !cardVideo)) return null;
    // A popover where the browser has one and the markup carries it (with its role and aria-modal):
    // showModal() re-styles the whole page behind it, a popover only itself. The written role, aria-modal
    // and focus inside keep the page out of a screen reader's reach; the page isn't made inert (that
    // costs what showModal() costs). Before Safari 17, showModal() as before.
    var canPopover = typeof HTMLElement === 'function' && typeof HTMLElement.prototype.showPopover === 'function' &&
      dialog.getAttribute('popover') === 'manual';
    if (!hasSound && soundBtn) soundBtn.hidden = true;

    // what the popup plays: its own video (the full MP4) when the card loops a light file, else the card's
    var popVideo = ownPop || cardVideo;
    // the popup never shows a picture as low as the card's: its own video loses the card-sized poster
    if (ownPop) ownPop.removeAttribute('poster');

    if (skipBlur()) root.setAttribute('data-ez-vi-noblur', '');

    // Two layers: the blur (a sibling of the dim scrim, so the scrim's fading opacity never becomes its
    // backdrop root) and the ring for a stall mid-play. The section's markup carries them; on markup
    // from before, they are made at the first open, which then costs a whole-page re-style once.
    var blurLayer = root.querySelector('[data-ez-vi-blur]');
    var bufferEl = root.querySelector('[data-ez-vi-buffer]');

    function ensureLayers() {
      if (!blurLayer && backdrop && backdrop.parentNode) {
        blurLayer = document.createElement('div');
        blurLayer.className = 'ez-vi-pop__blur';
        blurLayer.setAttribute('aria-hidden', 'true');
        backdrop.parentNode.insertBefore(blurLayer, backdrop);
      }
      if (!bufferEl && media && popVideo && kind !== 'placeholder') {
        bufferEl = document.createElement('span');
        bufferEl.className = 'ez-vi-pop__buffer';
        bufferEl.setAttribute('aria-hidden', 'true');
        bufferEl.innerHTML = '<span class="ez-vi-pop__spinner"></span>';
        var before = toggleBtn && toggleBtn.parentNode === media ? toggleBtn : null;
        media.insertBefore(bufferEl, before);
      }
    }

    // without a loop file the card's video is the full file: it moves into the popup while it is open
    var videoHome = cardVideo ? cardVideo.parentNode : null;
    var videoNext = cardVideo ? cardVideo.nextSibling : null;
    var videoInFrame = false;

    function moveVideo(intoFrame) {
      if (!cardVideo || ownPop || intoFrame === videoInFrame) return;
      videoInFrame = intoFrame;
      if (intoFrame) {
        media.insertBefore(cardVideo, media.firstChild);
        cardVideo.classList.add('is-popped');
      } else {
        videoHome.insertBefore(cardVideo, videoNext);
        cardVideo.classList.remove('is-popped');
      }
    }

    var shown = false;
    var isOpen = false;
    var usesModal = false;
    var usesPopover = false;
    var timer = null;
    var lastFocus = null;
    var watched = 0;
    var lastTime = 0;
    var furthest = 0;
    var cardPlays = false;
    var cardLoaded = false;
    var shownSent = false;
    var openSent = true;
    var closeSent = false;
    var ready = false;      // the current source is showing moving pictures
    var refused = false;    // the browser refused to play, even muted: the play mark waits for a tap
    var soundWanted = true; // for a video with sound: the visitor's last choice in this page view
    var primed = false;
    var watcher = null;
    var historyKey = null;
    var openedAt = 0;
    var tapAt = 0;
    var lagMs = null;
    var gapMs = null;
    var pendingAr = null;
    var leavingNow = false;
    var closedAs = '';
    var stillReady = false;
    var stillAsked = false;

    // ---------- the player's sources ----------

    var delivery = deliveryOverride || root.getAttribute('data-ez-vi-delivery') || 'mp4';
    var hlsUrl = root.getAttribute('data-ez-vi-hls') || '';
    var libUrl = root.getAttribute('data-ez-vi-lib') || '';
    var stillUrl = root.getAttribute('data-ez-vi-still') || '';
    var ownUrl = root.getAttribute('data-ez-vi-own') || '';
    var fileUrl = ownPop ? fullSrc : src;
    var chain = [];
    if (ownPop && delivery === 'adaptive' && hlsUrl && libUrl && !hasSound && canMse()) chain.push('hls');
    chain.push('file');
    if (ownPop && ownUrl && ownUrl !== fileUrl) chain.push('own');

    var player = popVideo && kind !== 'placeholder' ? new Player(popVideo, {
      chain: chain,
      hls: hlsUrl,
      lib: libUrl,
      file: fileUrl,
      own: ownUrl,
      attached: !ownPop,
      ar: parseFloat(frame.style.getPropertyValue('--ez-vi-ar')) || 0.5625,
      capPx: frame.classList.contains('has-caption') ? 80 : 0,
      on: onPlayer
    }) : null;

    // the still under the spinner, and the words of the error and offline states, made once at the start
    var still = null;
    if (ownPop && stillUrl) {
      still = document.createElement('img');
      still.className = 'ez-vi-pop__still';
      still.alt = '';
      still.setAttribute('aria-hidden', 'true');
      still.decoding = 'async';
      media.insertBefore(still, ownPop.nextSibling);
    }
    var note = null;
    var noteText = null;
    var retryBtn = null;
    if (player) {
      note = document.createElement('div');
      note.className = 'ez-vi-pop__note';
      noteText = document.createElement('p');
      noteText.className = 'ez-vi-pop__note-text';
      noteText.setAttribute('role', 'status');
      retryBtn = document.createElement('button');
      retryBtn.type = 'button';
      retryBtn.className = 'ez-vi-pop__retry';
      retryBtn.textContent = WORDS.retry;
      note.appendChild(noteText);
      note.appendChild(retryBtn);
      media.insertBefore(note, closeBtn.parentNode === media ? closeBtn : null);
    }

    function clarity() {
      try {
        if (typeof window.clarity === 'function') window.clarity.apply(window, arguments);
      } catch (e) {}
    }

    function track(name, extra) {
      if (editor) return;
      var params = { product_id: productId, product_name: productName, video_type: kind };
      if (extra) for (var k in extra) params[k] = extra[k];
      if (internal) params.traffic_type = 'internal';
      if (abParams) {
        params.ab_test = abParams.ab_test;
        params.ab_arm = abParams.ab_arm;
      }
      try {
        if (typeof window.gtag === 'function') window.gtag('event', name, params);
      } catch (e) {}
      clarity('event', name);
      log('event', name, params);
    }

    function emitDom(name) {
      try {
        root.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: { productId: productId } }));
      } catch (e) {}
    }

    function playQuietly(video) {
      try {
        video.muted = true;
        var p = video.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
      } catch (e) {}
    }

    function setTabbable(on) {
      [openBtn, dismissBtn].forEach(function (b) {
        if (!b) return;
        if (on) b.removeAttribute('tabindex');
        else b.setAttribute('tabindex', '-1');
      });
    }

    // ---------- the card ----------

    function loadCardVideo() {
      if (!cardVideo || cardLoaded) return;
      cardLoaded = true;
      var stillCard = reducedMotion() || lowData();
      if (poster && !cardVideo.getAttribute('poster')) cardVideo.setAttribute('poster', poster);
      if (!cardVideo.getAttribute('src')) cardVideo.setAttribute('src', src);
      if (stillCard && poster) return; // the poster alone stands for the video
      cardVideo.muted = true;
      cardVideo.preload = stillCard ? 'metadata' : 'auto';
      cardPlays = !stillCard;
      if (cardPlays) playQuietly(cardVideo);
      else {
        // preload was none: load() fetches again under the new preload, so the first frame shows
        try {
          cardVideo.load();
        } catch (e) {}
      }
    }

    function show() {
      if (shown) return;
      shown = true;
      clearTimeout(timer);
      loadCardVideo();
      card.hidden = false;
      setTabbable(true);
      // two frames, so the off-screen start is laid out before it slides in
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          card.classList.add('is-in');
        });
      });
      sendShown();
    }

    // the card's preview view: once per page view, when it has slid in on a page the visitor can see
    function sendShown() {
      if (shownSent || !shown || card.hidden || document.hidden) return;
      shownSent = true;
      track('video_instant_shown', { net_type: netType() });
    }

    function hideCard() {
      card.classList.remove('is-in');
      setTabbable(false);
      if (cardVideo) cardVideo.pause();
      var done = false;
      function finish() {
        if (done) return;
        done = true;
        card.hidden = true;
      }
      card.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, 400);
    }

    function onDismiss(e) {
      e.preventDefault();
      e.stopPropagation();
      if (!editor) rememberDismissed();
      track('video_instant_dismiss');
      hideCard();
    }

    // ---------- before the tap ----------

    // the full video's frame 0, decoded before it is needed, so its first paint never lands in the motion
    function loadStill() {
      if (!still || stillAsked) return;
      stillAsked = true;
      still.onload = function () {
        var d = typeof still.decode === 'function' ? still.decode() : null;
        function ok() {
          stillReady = true;
          showStill();
        }
        if (d && typeof d.then === 'function') d.then(ok, ok);
        else ok();
      };
      still.src = stillUrl;
    }

    // starts the popup's source, so it is there before the tap
    function primePop() {
      if (primed || !player || !ownPop) return;
      primed = true;
      if (watcher) {
        watcher.disconnect();
        watcher = null;
      }
      loadStill();
      player.prime();
      // the caption's font, so it is there when the popup opens
      try {
        if (document.fonts && typeof document.fonts.load === 'function') document.fonts.load('500 16px Lexend').catch(function () {});
      } catch (e) {}
    }

    // once the page has loaded and gone idle, with the card on or near the screen; or at its first touch
    function startEarly() {
      if (!ownPop || editor || lowData()) return;
      // a touch on the card's x is not a reach for the video
      function onTouch(e) {
        if (primed || lowData() || (dismissBtn && e && dismissBtn.contains(e.target))) return;
        primePop();
      }
      card.addEventListener('pointerdown', onTouch, { passive: true });
      card.addEventListener('touchstart', onTouch, { passive: true });
      afterLoadIdle(function () {
        if (primed || lowData()) return;
        if (typeof window.IntersectionObserver !== 'function') {
          primePop();
          return;
        }
        watcher = new IntersectionObserver(function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting) {
              primePop();
              return;
            }
          }
        }, { rootMargin: '400px' });
        watcher.observe(card);
      });
    }

    // ---------- the popup's states, all data- attributes ----------

    function setLabel(el, text) {
      if (el && el.getAttribute('aria-label') !== text) el.setAttribute('aria-label', text);
    }

    function setPaused(paused) {
      setFlag(frame, 'data-ez-vi-is-paused', paused);
      setLabel(toggleBtn, paused ? 'Play the video' : 'Pause the video');
      if (paused) setFlag(frame, 'data-ez-vi-is-buffering', false);
    }

    function setSoundButton() {
      if (!soundBtn || !hasSound || !popVideo) return;
      var on = !popVideo.muted;
      setFlag(soundBtn, 'data-ez-vi-is-on', on);
      setLabel(soundBtn, on ? 'Turn the sound off' : 'Turn the sound on');
    }

    function setNote(which) {
      if (!note) return;
      if (which) {
        var text = which === 'offline' ? WORDS.offline : WORDS.error;
        if (noteText.textContent !== text) noteText.textContent = text;
        retryBtn.hidden = which === 'offline';
      }
      setFlag(frame, 'data-ez-vi-note', which || false);
    }

    // while the source's first frame isn't up: the full video's own frame 0, never a low one; the video's
    // own frame when it has it at 720p or more, else the still once decoded, else the skeleton
    function showStill() {
      if (!isOpen || ready) {
        setFlag(frame, 'data-ez-vi-still', false);
        return;
      }
      var v = ownPop;
      var own = !!(v && v.readyState >= 2 && (v.currentTime || 0) < 0.1 && shortSide(v.videoWidth, v.videoHeight) >= FLOOR);
      setFlag(frame, 'data-ez-vi-still', own ? 'video' : stillReady ? 'img' : false);
    }

    function setReady(on) {
      ready = on;
      if (on) {
        setFlag(frame, 'data-ez-vi-is-loading', false);
        setFlag(frame, 'data-ez-vi-still', false);
        setNote(false);
      } else if (isOpen) {
        setFlag(frame, 'data-ez-vi-is-loading', true);
        showStill();
      }
      setFlag(ownPop, 'data-ez-vi-is-ready', on);
    }

    function onPlayer(name) {
      if (!isOpen) return;
      if (name === 'frame') {
        refused = false;
        setPaused(false);
        setReady(true);
        var m = player.report();
        if (m.first && m.firstAt - tapAt > 3000) clarity('event', 'video_instant_slow');
      } else if (name === 'stall') {
        // the ring shows 500 ms into it (in the CSS), so a short stall never flashes it
        if (ready) setFlag(frame, 'data-ez-vi-is-buffering', true);
        clarity('event', 'video_instant_stall');
      } else if (name === 'stallend') {
        setFlag(frame, 'data-ez-vi-is-buffering', false);
        if (frame.getAttribute('data-ez-vi-note') === 'offline') setNote(false);
      } else if (name === 'path') {
        // a new source: its picture isn't up yet
        if (ready) setReady(false);
        setFlag(frame, 'data-ez-vi-is-buffering', false);
      } else if (name === 'loaded') {
        showStill();
      } else if (name === 'offline') {
        setNote('offline');
      } else if (name === 'online') {
        if (frame.getAttribute('data-ez-vi-note') === 'offline') setNote(false);
      } else if (name === 'error') {
        setFlag(frame, 'data-ez-vi-is-buffering', false);
        setFlag(frame, 'data-ez-vi-is-loading', false);
        showStill();
        setNote('error');
        clarity('event', 'video_instant_error');
      } else if (name === 'muted') {
        setSoundButton();
      } else if (name === 'refused') {
        // the browser refused playback, even muted: the frame 0 still with the play mark, ready for a tap
        refused = true;
        setFlag(frame, 'data-ez-vi-is-loading', false);
        showStill();
        setPaused(true);
      }
    }

    // ---------- the popup ----------

    function onTimeUpdate() {
      var t = popVideo.currentTime || 0;
      if (!isOpen) {
        lastTime = t;
        return;
      }
      var step = t - lastTime;
      if (step > 0 && step < 1.5) watched += step;
      lastTime = t;
      if (t > furthest) furthest = t;
    }

    // the frame takes the video's own shape; the loop file has the full video's shape. A shape that
    // arrives while the frame is moving waits for the motion to end, so the frame doesn't jump.
    function applyAr(ar) {
      if (anims.length) {
        pendingAr = ar;
        return;
      }
      pendingAr = null;
      frame.style.setProperty('--ez-vi-ar', ar);
      if (player) player.o.ar = parseFloat(ar) || player.o.ar;
    }

    function onMeta(e) {
      var v = e && e.target;
      if (!v) return;
      var w = v.videoWidth;
      var h = v.videoHeight;
      if (w > 0 && h > 0) {
        var ar = Math.min(1.8, Math.max(0.5, w / h)).toFixed(4);
        if (frame.style.getPropertyValue('--ez-vi-ar') !== ar) applyAr(ar);
      }
    }

    // the caption's length, from the video itself when the details gave none
    function onDuration() {
      if (!lenEl || !popVideo || lenEl.textContent.replace(/\s+/g, '')) return;
      var d = popVideo.duration;
      if (!isFinite(d) || d <= 0) return;
      lenEl.textContent = clock(d);
      if (lenWrap) lenWrap.hidden = false;
    }

    function startPlayback(gesture) {
      if (!player) return;
      refused = false;
      lastTime = 0;
      // sound from the tap only for a video kept with sound; the tap is the visitor's gesture
      popVideo.muted = !(hasSound && soundWanted && gesture);
      setSoundButton();
      setPaused(false);
      player.play(tapAt);
    }

    // the tap's wait for its first frame, and the longest wait between frames in the 600 ms after it
    function probeFrames() {
      lagMs = null;
      gapMs = null;
      if (typeof window.requestAnimationFrame !== 'function') return;
      var last = 0;
      var end = tapAt + 600;
      function stepFrame(ts) {
        if (!last) {
          lagMs = Math.max(0, ts - tapAt);
          gapMs = 0;
        } else if (ts - last > gapMs) {
          gapMs = ts - last;
        }
        last = ts;
        if (ts < end && isOpen) window.requestAnimationFrame(stepFrame);
      }
      window.requestAnimationFrame(stepFrame);
    }

    // ---------- the popup's motion: the frame grows out of the card and shrinks back into it ----------

    // Transform and opacity only, so the compositor runs every frame of it while the main thread works.
    // The frame keeps its own 16px corner (border-radius is never animated: in Chrome one animated
    // radius puts the whole animation back on the main thread); its first frames fade in over the card
    // and its last fade out into it, which hides the smaller corner at the card's size.

    var anims = []; // the motion running now
    var phase = ''; // 'in' or 'out' while it runs
    var cardFade = null; // holds the card hidden while the popup is open, and brings it back
    var blurAnim = null; // brings the blur in once the frame has landed, and holds it
    var cardH = 0;

    function noBlur() {
      return !blurLayer || root.hasAttribute('data-ez-vi-noblur');
    }

    function stopAnims() {
      for (var i = 0; i < anims.length; i++) {
        try {
          anims[i].cancel();
        } catch (e) {}
      }
      anims = [];
      endMotion();
    }

    function endMotion() {
      phase = '';
      window.removeEventListener('resize', onMotionResize);
      if (pendingAr && !anims.length) applyAr(pendingAr);
    }

    // a turn of the phone or a resize mid-motion: the motion jumps to its end, laid out anew
    function onMotionResize() {
      for (var i = 0; i < anims.length; i++) {
        try {
          anims[i].finish();
        } catch (e) {}
      }
    }

    function canAnimate() {
      return typeof frame.animate === 'function';
    }

    function canMorph() {
      return shown && !card.hidden && canAnimate() && !reducedMotion();
    }

    // the transform that puts the open frame where the card is: same centre, same height, card's tilt.
    // c is the card's box as the screen draws it, f the open frame's.
    function cardTransform(c, f) {
      if (!c || !c.width || !f || !f.width || !f.height || !cardH) return null;
      var dx = c.left + c.width / 2 - (f.left + f.width / 2);
      var dy = c.top + c.height / 2 - (f.top + f.height / 2);
      var s = cardH / f.height;
      return 'translate(' + dx.toFixed(1) + 'px, ' + dy.toFixed(1) + 'px) rotate(' + tilt + 'deg) scale(' + s.toFixed(4) + ')';
    }

    // held: the motion holds its last frame (the close) until finishClose cancels it
    function onMotionEnd(list, done, held) {
      var finished = false;
      function finish() {
        if (finished) return;
        finished = true;
        clearTimeout(guard);
        if (anims === list && !held) {
          anims = [];
          endMotion();
        }
        if (done) done();
      }
      // a hidden page draws no frames and sends no finish: the timer stands in
      var guard = setTimeout(finish, 500);
      list[0].onfinish = finish;
      list[0].oncancel = function () {
        clearTimeout(guard);
        finished = true;
      };
    }

    function animateIn(from) {
      stopAnims();
      if (!canAnimate()) return;
      var rm = reducedMotion();
      var list = [];
      if (rm) {
        list.push(frame.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 150, easing: 'linear' }));
      } else if (from) {
        list.push(frame.animate([
          { transform: from, opacity: 0, offset: 0 },
          { opacity: 1, offset: 0.2 },
          { transform: 'none', opacity: 1, offset: 1 }
        ], { duration: 300, easing: ENTER }));
      } else {
        list.push(frame.animate([{ opacity: 0, transform: 'scale(0.94)' }, { opacity: 1, transform: 'none' }], { duration: 300, easing: ENTER }));
      }
      if (backdrop) list.push(backdrop.animate([{ opacity: 0 }, { opacity: 1 }], { duration: rm ? 150 : 260, easing: 'linear' }));
      // the card goes as the frame leaves it, and stays gone while the popup is open
      if (cardFade) cardFade.cancel();
      cardFade = card.animate([{ opacity: 1 }, { opacity: 0 }], { duration: rm ? 150 : 60, easing: 'linear', fill: 'forwards' });
      // the blur only once the frame has landed, so the GPU never draws both at once
      if (blurAnim) blurAnim.cancel();
      blurAnim = noBlur() ? null : blurLayer.animate([{ opacity: 0 }, { opacity: 1 }], { duration: rm ? 150 : 200, delay: rm ? 0 : 280, easing: 'linear', fill: 'both' });
      anims = list;
      phase = 'in';
      window.addEventListener('resize', onMotionResize);
      onMotionEnd(list, function () {
        if (isOpen) lockScroll();
      });
    }

    function blurNow() {
      if (!blurAnim || !blurAnim.effect || typeof blurAnim.effect.getComputedTiming !== 'function') return blurAnim ? 1 : 0;
      var p = blurAnim.effect.getComputedTiming().progress;
      return typeof p === 'number' ? p : 0;
    }

    function fadeBlurOut(list, ms) {
      if (!blurAnim) return;
      var from = blurNow();
      blurAnim.cancel();
      blurAnim = null;
      if (from > 0) list.push(blurLayer.animate([{ opacity: from }, { opacity: 0 }], { duration: ms, easing: 'linear', fill: 'forwards' }));
    }

    function animateOut(to, done) {
      if (!canAnimate()) return done();
      stopAnims();
      var rm = reducedMotion();
      var list = [];
      if (rm) {
        list.push(frame.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 120, easing: 'linear', fill: 'forwards' }));
      } else if (to) {
        list.push(frame.animate([
          { transform: 'none', opacity: 1, offset: 0 },
          { opacity: 1, offset: 0.7 },
          { transform: to, opacity: 0, offset: 1 }
        ], { duration: 220, easing: EXIT, fill: 'forwards' }));
      } else {
        list.push(frame.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'scale(0.94)' }], { duration: 220, easing: EXIT, fill: 'forwards' }));
      }
      if (backdrop) list.push(backdrop.animate([{ opacity: 1 }, { opacity: 0 }], { duration: rm ? 120 : 220, easing: 'linear', fill: 'forwards' }));
      // the blur goes in four frames: drawing it under the moving frame is what costs the GPU most
      fadeBlurOut(list, 60);
      // the card comes back as the frame lands in it (the same eased progress, so they meet)
      if (cardFade) cardFade.cancel();
      cardFade = card.animate(
        to ? [{ opacity: 0, offset: 0 }, { opacity: 0, offset: 0.7 }, { opacity: 1, offset: 1 }] : [{ opacity: 0 }, { opacity: 1 }],
        { duration: rm ? 120 : 220, easing: to ? EXIT : 'linear', fill: 'forwards' }
      );
      anims = list;
      phase = 'out';
      window.addEventListener('resize', onMotionResize);
      onMotionEnd(list, done, true);
    }

    // closed while it was still opening (Back or the x during the grow): the open runs backwards from
    // where it is, holding its first frame at the end so nothing flashes before the dialog shuts
    function reverseIn(done) {
      var list = anims.slice();
      if (!list.length) return animateOut(null, done);
      for (var i = 0; i < list.length; i++) {
        try {
          list[i].effect.updateTiming({ fill: 'both' });
          list[i].reverse();
        } catch (e) {}
      }
      fadeBlurOut(list, 60);
      var t = 0;
      try {
        t = list[0].currentTime || 0;
      } catch (e) {}
      if (cardFade) cardFade.cancel();
      cardFade = card.animate([{ opacity: 0 }, { opacity: 1 }], { duration: Math.max(60, Math.min(300, t)), easing: 'linear', fill: 'forwards' });
      anims = list;
      phase = 'out';
      onMotionEnd(list, done, true);
    }

    // ---------- history: Back closes the popup ----------

    // the entry the open adds; Back takes it away and closes the popup
    function addEntry() {
      if (editor || !window.history || typeof window.history.pushState !== 'function') return;
      historyKey = 'ez-vi-' + Date.now() + '-' + Math.random().toString(36).slice(2);
      try {
        window.history.pushState({ ezVi: historyKey }, '');
        window.addEventListener('popstate', onPopState);
      } catch (e) {
        historyKey = null;
      }
    }

    function onPopState() {
      window.removeEventListener('popstate', onPopState);
      historyKey = null;
      if (isOpen) close('back');
    }

    // closed another way: go back through the entry, so no stray one is left and the next Back leaves
    function dropEntry() {
      window.removeEventListener('popstate', onPopState);
      var key = historyKey;
      historyKey = null;
      if (!key) return;
      try {
        var state = window.history.state;
        if (state && state.ezVi === key) window.history.back();
      } catch (e) {}
    }

    var closing = false;

    function sinceOpen() {
      return now() - openedAt;
    }

    // The tap: reads first, while the layout is still clean; playback inside the gesture; every DOM
    // write in one batch; one read for where the frame sits; the motion; the rest after its first frame.
    // e: the tap's event when a tap opened it; how: 'hash' (#up-close at the start, so no gesture and
    // muted) or 'request' (ez-vi:open-request).
    function open(e, how) {
      if (isOpen || closing) return;
      var t0 = now();
      tapAt = e && e.timeStamp > 0 && e.timeStamp <= t0 + 1000 ? e.timeStamp : t0;
      var morph = canMorph();
      var cardRect = null;
      if (morph) {
        cardRect = card.getBoundingClientRect();
        cardH = card.offsetHeight;
      }
      isOpen = true;
      closeSent = false;
      openSent = false;
      closedAs = '';
      ready = false;
      openedAt = t0;
      lastFocus = document.activeElement;
      watched = 0;
      lastTime = 0;
      furthest = 0;
      // playback starts inside the tap, so the browser counts it as the visitor's gesture (sound needs it)
      startPlayback(how !== 'hash');
      ensureLayers();
      setFlag(frame, 'data-ez-vi-is-loading', !!player);
      setFlag(ownPop, 'data-ez-vi-is-ready', false);
      setFlag(frame, 'data-ez-vi-is-buffering', false);
      setFlag(frame, 'data-ez-vi-note', false);
      showStill();
      // without a loop file the card's video is the one: it moves into the frame
      if (!ownPop) moveVideo(true);
      if (canPopover) {
        try {
          dialog.showPopover();
          usesPopover = true;
        } catch (err) {
          usesPopover = false;
        }
      }
      if (!usesPopover && typeof dialog.showModal === 'function') {
        try {
          dialog.showModal();
          usesModal = true;
        } catch (err) {
          usesModal = false;
        }
      }
      if (!usesPopover && !usesModal) dialog.setAttribute('open', '');
      addEntry();
      // focus brings the style up to date: the popup's own as a popover, the whole page's as a modal
      try {
        closeBtn.focus({ preventScroll: true });
      } catch (err) {
        closeBtn.focus();
      }
      animateIn(morph ? cardTransform(cardRect, frame.getBoundingClientRect()) : null);
      probeFrames();
      afterPaint(function () {
        if (!isOpen) return;
        // the card's loop pauses while the popup is open
        if (ownPop && cardVideo) cardVideo.pause();
        loadStill();
        sendOpen();
        // with no motion to wait for, the page is held now; otherwise when the frame has landed
        if (!anims.length) lockScroll();
      });
    }

    function sendOpen() {
      if (openSent) return;
      openSent = true;
      track('video_instant_open', { net_type: netType() });
      emitDom('ez-vi:open');
    }

    // after the frame is back in the card: shut the dialog, give the page back, let the card play.
    // Shutting the dialog re-styles the page; by now the card shows and nothing moves.
    function finishClose() {
      closing = false;
      if (usesPopover) {
        try {
          dialog.hidePopover();
        } catch (e) {}
      } else if (usesModal && dialog.open) {
        try {
          dialog.close();
        } catch (e) {
          dialog.removeAttribute('open');
        }
      } else {
        dialog.removeAttribute('open');
      }
      usesModal = false;
      usesPopover = false;
      stopAnims();
      if (blurAnim) {
        blurAnim.cancel();
        blurAnim = null;
      }
      unlockScroll();
      setFlag(frame, 'data-ez-vi-is-loading', false);
      setFlag(frame, 'data-ez-vi-is-buffering', false);
      setFlag(frame, 'data-ez-vi-still', false);
      setNote(false);
      ready = false;
      if (ownPop) {
        setFlag(ownPop, 'data-ez-vi-is-ready', false);
        ownPop.muted = true;
      }
      if (cardVideo) {
        moveVideo(false);
        cardVideo.muted = true;
      }
      if (cardFade) {
        cardFade.cancel();
        cardFade = null;
      }
      if (cardVideo && cardPlays && shown && !card.hidden && !document.hidden) playQuietly(cardVideo);
      var back = lastFocus && document.contains(lastFocus) ? lastFocus : openBtn;
      try {
        back.focus({ preventScroll: true });
      } catch (e) {}
    }

    // how the open stood when it closed: playing, stalled, loading, refused, or error_<the last failure>
    function endState() {
      var m = player ? player.report() : null;
      if (!m) return 'loading';
      if (m.failed) return { 1: 'error_aborted', 2: 'error_network', 3: 'error_decode' }[m.errorCode] || 'error_src';
      if (m.first && (m.stalled || m.holding || !ready)) return 'stalled';
      if (m.first) return 'playing';
      return refused ? 'refused' : 'loading';
    }

    // one close per open: whichever comes first, the popup's own close or the visitor leaving
    function sendClose(method, leaving) {
      if (closeSent) return;
      closeSent = true;
      sendOpen();
      var duration = popVideo && isFinite(popVideo.duration) ? popVideo.duration : 0;
      var extra = {
        seconds_watched: Math.round(watched * 10) / 10,
        percent_watched: duration > 0 ? Math.min(100, Math.round((furthest / duration) * 100)) : 0,
        close_method: method,
        net_type: netType()
      };
      var m = player ? player.report() : null;
      if (m) {
        extra.end_state = closedAs || endState();
        if (m.first) {
          extra.first_frame_ms = Math.max(0, Math.round(m.firstAt - tapAt));
          if (m.videoH > 0) extra.video_h = m.videoH;
        }
        extra.stall_count = m.stallCount;
        extra.stall_ms = Math.round(m.stallMs);
        if (m.firstStallS !== null) extra.first_stall_s = Math.round(m.firstStallS * 10) / 10;
      }
      if (lagMs !== null) extra.open_lag_ms = Math.round(lagMs);
      if (gapMs !== null) extra.open_max_gap_ms = Math.round(gapMs);
      if (leaving || leavingNow) extra.transport_type = 'beacon';
      if (m) {
        var ff = extra.first_frame_ms;
        clarity('set', 'video_product', handle || productId);
        clarity('set', 'video_net', extra.net_type);
        clarity('set', 'video_first_frame', ff === undefined ? 'none' : ff < 1000 ? '<1s' : ff <= 3000 ? '1-3s' : '>3s');
        clarity('set', 'video_stalls', m.stallCount === 0 ? '0' : m.stallCount <= 2 ? '1-2' : '3+');
        clarity('set', 'video_end', extra.end_state);
        if (internal) clarity('set', 'internal', 'yes');
      }
      track('video_instant_close', extra);
    }

    function close(method) {
      if (!isOpen) return;
      if (popVideo) onTimeUpdate();
      closedAs = endState();
      isOpen = false;
      // the picture and the sound stop first
      if (player) player.close();
      else if (popVideo) popVideo.pause();
      if (method === 'teardown' || method === 'other') {
        dropEntry();
        if (method !== 'teardown') {
          sendClose(method);
          emitDom('ez-vi:close');
        }
        finishClose();
        return;
      }
      closing = true;
      if (phase === 'in' && anims.length) {
        reverseIn(finishClose);
      } else {
        // reads before any write: the frame is where it was laid out, the card where it sits
        var to = null;
        if (canMorph()) {
          cardH = card.offsetHeight;
          to = cardTransform(card.getBoundingClientRect(), frame.getBoundingClientRect());
        }
        animateOut(to, finishClose);
      }
      setFlag(frame, 'data-ez-vi-is-buffering', false);
      // after the close's first frame: the history entry, the event and the hook (at once if the page is left)
      afterPaint(function () {
        dropEntry();
        sendClose(method);
        emitDom('ez-vi:close');
      });
    }

    function onToggle(e) {
      e.preventDefault();
      // the second tap of a quick double tap on the card lands here: it is not a pause
      if (!popVideo || !player || (!ready && !refused) || sinceOpen() < 400) return;
      if (popVideo.paused && !player.holding) {
        // the visitor's tap: play() inside it
        refused = false;
        popVideo.muted = !(hasSound && soundWanted);
        setSoundButton();
        setPaused(false);
        if (!ready) setReady(false);
        player.resume();
      } else {
        player.paused();
        popVideo.pause();
        setPaused(true);
      }
    }

    function onRetry(e) {
      e.preventDefault();
      e.stopPropagation();
      if (!player || !isOpen) return;
      setNote(false);
      setReady(false);
      player.retry();
    }

    function onSound(e) {
      e.preventDefault();
      e.stopPropagation();
      if (!popVideo || !hasSound) return;
      soundWanted = popVideo.muted;
      popVideo.muted = !soundWanted;
      setSoundButton();
    }

    function onVolume() {
      // the card is always silent, and so is a video without sound
      if (!hasSound || !isOpen) {
        if (!popVideo.muted) popVideo.muted = true;
        return;
      }
      setSoundButton();
    }

    // keeps Tab inside the popup when the browser has no modal dialog
    function onKeydown(e) {
      if (!isOpen) return;
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        close('esc');
        return;
      }
      if (e.key !== 'Tab' || usesModal) return;
      var items = Array.prototype.filter.call(
        dialog.querySelectorAll('button:not([hidden])'),
        function (b) { return b.offsetParent !== null; }
      );
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function onVisibility() {
      if (isOpen && player) {
        if (document.hidden) player.sleep();
        else player.wake();
      }
      if (!cardVideo || !cardPlays || isOpen || !shown || card.hidden) return;
      if (document.hidden) cardVideo.pause();
      else playQuietly(cardVideo);
    }

    // the visitor hides or leaves the page with the popup open: that open's close goes now, as leave.
    // The popup stays as it is; if the visitor comes back to it, its later close sends nothing.
    function onLeave() {
      // a close still waiting for its first frame goes now, as a beacon
      leavingNow = true;
      flushLater();
      leavingNow = false;
      if (!isOpen || closeSent) return;
      if (popVideo) onTimeUpdate();
      sendClose('leave', true);
    }

    // on window in the capture phase, so it runs before the page's other listeners (gtag's flush among them)
    function onPageVisibility() {
      if (document.hidden) onLeave();
      else sendShown();
    }

    // the sharing code (or anything else) asks for the popup: on the root, or on the document for this product
    function onOpenRequest(e) {
      if (e.currentTarget === document) {
        if (root.contains(e.target)) return; // the root's own listener has it
        var pid = e.detail && e.detail.productId;
        if (pid && String(pid) !== productId) return;
      }
      open(null, 'request');
    }

    function onHash() {
      if (wantsHash() && !isOpen && !closing) open(null, 'hash');
    }

    openBtn.addEventListener('click', function (e) {
      e.preventDefault();
      open(e);
    });
    if (dismissBtn) dismissBtn.addEventListener('click', onDismiss);
    closeBtn.addEventListener('click', function (e) {
      e.preventDefault();
      close('button');
    });
    // a tap on the backdrop in the first 400 ms is the second half of a double tap on the card
    if (backdrop) backdrop.addEventListener('click', function () { if (sinceOpen() >= 400) close('backdrop'); });
    // a cancel with no Esc before it is the phone's back (Chrome on Android closes a modal dialog with it)
    dialog.addEventListener('cancel', function (e) {
      e.preventDefault();
      close(Date.now() - escAt < 500 ? 'esc' : 'back');
    });
    dialog.addEventListener('close', function () {
      if (isOpen) close('other');
      else if (closing) finishClose();
    });
    // the popover hidden by the browser rather than by its own close
    dialog.addEventListener('toggle', function (e) {
      if (usesPopover && e && e.newState === 'closed' && isOpen) close('other');
    });
    dialog.addEventListener('keydown', onKeydown);
    if (toggleBtn) toggleBtn.addEventListener('click', onToggle);
    if (retryBtn) retryBtn.addEventListener('click', onRetry);
    if (soundBtn && hasSound) soundBtn.addEventListener('click', onSound);
    if (cardVideo) cardVideo.addEventListener('loadedmetadata', onMeta);
    if (popVideo) {
      popVideo.addEventListener('timeupdate', onTimeUpdate);
      popVideo.addEventListener('seeking', function () { lastTime = popVideo.currentTime || 0; });
      popVideo.addEventListener('play', function () {
        if (!isOpen) return;
        setPaused(false);
        if (player) player.played_();
      });
      popVideo.addEventListener('pause', function () {
        // the player's own holds behind the spinner are not the visitor's pause
        if (!isOpen || !ready || (player && (player.holding || !player.wantPlay))) return;
        if (player) player.paused();
        setPaused(true);
      });
      popVideo.addEventListener('loadedmetadata', onDuration);
      popVideo.addEventListener('durationchange', onDuration);
      popVideo.addEventListener('volumechange', onVolume);
      if (popVideo.readyState >= 1) onDuration();
    }
    if (ownPop) ownPop.addEventListener('loadedmetadata', onMeta);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('visibilitychange', onPageVisibility, true);
    window.addEventListener('pagehide', onLeave, true);
    if (!editor) {
      root.addEventListener('ez-vi:open-request', onOpenRequest);
      document.addEventListener('ez-vi:open-request', onOpenRequest);
      window.addEventListener('hashchange', onHash);
    }

    // a card with no poster (our own MP4) shows its loop's own first frame, fetched before the slide-in
    if (cardVideo && !poster && kind !== 'placeholder' && !lowData()) {
      try {
        if (cardVideo.preload === 'none') cardVideo.preload = 'metadata';
      } catch (e) {}
    }

    var api = {
      show: show,
      open: open,
      close: close,
      player: player,
      // what a test or ?es_debug=1 reads
      report: function () {
        var m = player ? player.report() : {};
        m.chain = chain.slice();
        m.delivery = delivery;
        m.lagMs = lagMs;
        m.gapMs = gapMs;
        m.tapAt = tapAt;
        m.open = isOpen;
        m.ready = ready;
        return m;
      },
      teardown: function () {
        clearTimeout(timer);
        if (watcher) {
          watcher.disconnect();
          watcher = null;
        }
        close('teardown');
        dropEntry();
        if (player) player.destroy();
        document.removeEventListener('visibilitychange', onVisibility);
        window.removeEventListener('visibilitychange', onPageVisibility, true);
        window.removeEventListener('pagehide', onLeave, true);
        document.removeEventListener('ez-vi:open-request', onOpenRequest);
        window.removeEventListener('hashchange', onHash);
      }
    };
    root.__ezVi = api;
    log('chain', chain.join(' > '), 'delivery', delivery);

    if (editor || !wasDismissed()) {
      timer = setTimeout(show, delay * 1000);
      startEarly();
    } else {
      card.hidden = true; // dismissed earlier in this browsing session: at run time only, never in the markup
    }
    // #up-close opens the popup at the start, muted, whether or not the card was dismissed
    if (!editor && wantsHash()) afterFrame(onHash);
    return api;
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-ez-vi]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  window.EzVideoInstant = { init: init, initAll: initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // theme editor: a re-rendered section starts again, a removed one cleans up, a selected one shows
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var roots = e.target.querySelectorAll('[data-ez-vi]');
    for (var i = 0; i < roots.length; i++) if (roots[i].__ezVi) roots[i].__ezVi.teardown();
  });
  document.addEventListener('shopify:section:select', function (e) {
    var roots = e.target.querySelectorAll('[data-ez-vi]');
    for (var i = 0; i < roots.length; i++) if (roots[i].__ezVi) roots[i].__ezVi.show();
  });
})();
